import { useState } from 'react';
import './ProjectShowcase.css';

// Reusable template for showcasing a single project inside a gallery-style modal
// (e.g. each model in "3D Art & Animations"). Renders a title, a tools list
// (styled like the project card tools), a side column of selectable thumbnails
// (left) and a large preview (right), each thumbnail with its own subtitle.
//
// media item shape:
// {
//   type: 'image' | 'video' | 'embed', // 'video' = local file, played gif-style (autoplay, loop, muted, no controls); 'embed' = iframe (e.g. YouTube)
//   thumbnail: string,  // required static image asset shown in the side column (videos never autoplay as thumbnails)
//   src: string,        // asset shown in the large preview when selected: image src, video src, or embed URL
//   subtitle: string,   // e.g. 'Base color', 'Wireframe', 'Final render'
//   alt?: string,       // optional alt text for image previews
//   playbackRate?: number, // 'video' only: playback speed, e.g. 0.75 for slow motion. Defaults to 1.
// }
//
// All 'video' media items are always mounted and playing in sync (not
// remounted when the selection changes); only the selected one is shown.
// This makes switching between e.g. Wireframe/BaseColor/Final feel like
// swapping a view rather than restarting playback.
function ProjectShowcase({ title, tools = [], media = [] }) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selected = media[selectedIndex];

  return (
    <div className="project-showcase">
      <h3 className="project-showcase__title">{title}</h3>
      {tools.length > 0 && (
        <ul className="project-showcase__tools">
          {tools.map((tool) => <li key={tool}>{tool}</li>)}
        </ul>
      )}

      {selected && (
        <div className="project-showcase__viewer">
          {media.length > 1 && (
            <div className="project-showcase__thumbs">
              {media.map((item, index) => (
                <button
                  className={`project-showcase__thumb${index === selectedIndex ? ' project-showcase__thumb--active' : ''}`}
                  key={`${item.subtitle}-${item.src}`}
                  onClick={() => setSelectedIndex(index)}
                  type="button"
                >
                  <span className="project-showcase__thumb-media">
                    <img alt="" src={item.thumbnail} />
                  </span>
                  {item.subtitle && <span className="project-showcase__thumb-label">{item.subtitle}</span>}
                </button>
              ))}
            </div>
          )}

          <div className="project-showcase__preview">
            {media.map((item, index) => {
              const isActive = index === selectedIndex;

              if (item.type === 'video') {
                // Always mounted so every video keeps playing in sync; only
                // the active one is made visible.
                return (
                  <video
                    aria-hidden={!isActive}
                    autoPlay
                    className={`project-showcase__preview-video${isActive ? ' project-showcase__preview-video--active' : ''}`}
                    key={item.src}
                    loop
                    muted
                    playsInline
                    ref={(video) => {
                      if (video) {
                        video.playbackRate = item.playbackRate ?? 1;
                      }
                    }}
                    src={item.src}
                  />
                );
              }

              if (!isActive) return null;

              if (item.type === 'embed') {
                return (
                  <iframe
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="project-showcase__preview-embed"
                    key={item.src}
                    src={item.src}
                    title={item.subtitle || title}
                  />
                );
              }

              return (
                <img
                  alt={item.alt || item.subtitle || ''}
                  className="project-showcase__preview-image"
                  key={item.src}
                  src={item.src}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectShowcase;
